<template>
  <!--
    Vue's transition component wraps the element that will be animated.
    The 'name' attribute ("fade" in this case) is used as a prefix for the CSS classes.
    The transition is triggered by the v-if directive on the child element.
  -->
  <transition name="fade">
    <div v-if="showPopup" class="warning-popup-overlay" @click.self="close">
      <!--
        The :class="{ visible: showPopup }" has been removed from this div,
        as the animation is now controlled by the <transition> wrapper.
      -->
      <div class="warning-popup">
        <div class="icon-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h3>温馨提示</h3>
        <p class="warning-message" v-html="displayMessage" />

        <div class="warning-actions">
          <button
            v-for="action in actions"
            :key="action.id"
            :class="action.classList"
            @click="handleAction(action.id)"
          >
            {{ action.text }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'UpdatePersonalDataWarningPopup',
  props: {
    errorMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // Controls the presence of the popup in the DOM via v-if
      showPopup: false,
      // The message to be displayed, with HTML formatting
      displayMessage: '',
      // Array of button configurations
      actions: []
    }
  },
  watch: {
    errorMessage() {
      this.initPopup()
    }
  },
  mounted() {
    this.initPopup()
  },
  methods: {
    initPopup() {
      // Initialize local variables from the component's errorMessage prop/data
      let localDisplayMessage = this.errorMessage
      let localActions = []

      // Define conditions for different popup scenarios
      const isInfoMismatch = this.errorMessage.includes('证件信息不一致')
      // Check for Registration ID exists
      const isIdExisting = this.errorMessage === '身份证已经存在，请更换身份证再注册!'
      // Check for Update ID exists
      const isUpdateFailed = this.errorMessage.includes('修改失敗, 身份证己经存在')

      if (isInfoMismatch) {
        // Case 1: For "Information Mismatch" -> Show two buttons
        localDisplayMessage =
      '证件信息不一致！确认仅可开通电子钱包通道，建议重新输入。'
        localActions = [
          { id: 'confirm', text: '确认', classList: 'popup-btn btn-secondary' },
          { id: 'reset', text: '重新填写', classList: 'popup-btn btn-primary' }
        ]
      } else if (isIdExisting || isUpdateFailed) {
        // Case 2: For "ID Exists" (Registration or Update) -> Show a single "Acknowledge" button
        // The original errorMessage is used for the display message.
        localActions = [
          { id: 'acknowledge', text: '我知道了', classList: 'popup-btn btn-secondary' }
        ]
      } else {
        // Default Case: For all other messages -> Show a single "Confirm" button
        localActions = [
          { id: 'confirm', text: '确认', classList: 'popup-btn btn-secondary' }
        ]
      }

      // Update component data to render the popup
      this.displayMessage = localDisplayMessage.replace(/([！。])/g, '$1<br>')
      this.actions = localActions

      // Show the popup only if there is an error message
      this.showPopup = !!this.errorMessage
    },
    /**
       * Hides the popup and resolves the promise with the selected action.
       * @param {string} actionId - The ID of the action that was triggered.
       */
    handleAction(actionId) {
      this.close()
      this.$emit('onButtonPress', actionId)
    },

    /**
       * Hides the warning popup with an animation.
       * Resolves the promise with null if closed via overlay click.
       */
    close() {
      this.showPopup = false
    }
  }
}
</script>

<style scoped>
/* Scoped styles ensure they only apply to this component */
.warning-popup-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  z-index: 50;
}

.warning-popup {
  background-color: white;
  border-radius: 0.25rem; /* rounded-xl */
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.1); /* shadow-2xl */
  width: 100%;
  max-width: 24rem; /* max-w-sm */
  margin: auto;
  padding: 0.5rem;
  text-align: center;
}

/* --- Vue Transition Classes --- */

/* Define the transition properties for both enter and leave animations.
  The overlay will fade, and the popup will transform.
*/
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-active .warning-popup,
.fade-leave-active .warning-popup {
  transition: all 0.3s ease;
}

/* Define the starting state for the enter animation and the
  ending state for the leave animation.
*/
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.fade-enter .warning-popup,
.fade-leave-to .warning-popup {
  transform: scale(0.95);
  opacity: 0;
}

.icon-container {
  margin: 0 auto 0.5rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 0.75rem;
  width: 0.75rem;
  border-radius: 9999px; /* rounded-full */
  background-color: #fef3c7; /* bg-yellow-100 */
}

.icon-container svg {
  height: 0.5rem;
  width: 0.5rem;
  color: #d97706; /* text-yellow-600 */
}

.warning-popup h3 {
  font-size: 0.4rem; /* text-lg */
  line-height: 1.5; /* leading-6 */
  font-weight: 500; /* font-medium */
  color: #111827; /* text-gray-900 */
  margin: 0 0 0.5rem 0;
}

.warning-message {
  font-size: 0.25rem; /* text-sm */
  color: #4b5563; /* text-gray-600 */
  margin: 0 0 0.5rem 0;
  word-break: keep-all; /* Prevent breaking in the middle of words */
}

.warning-actions {
  display: flex;
  justify-content: space-evenly;
}

.popup-btn {
  display: flex;
  justify-content: center;
  width: 40%;
  border-radius: 0.25rem; /* rounded-md */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
  padding: 0.2rem;
  font-size: 0.25rem; /* text-base */
  font-weight: 500; /* font-medium */
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.popup-btn:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  --ring-offset-shadow: 0 0 0 2px #ffffff;
  box-shadow: var(--ring-offset-shadow), var(--ring-shadow), var(--shadow);
}

.btn-primary {
  background-color: #fbbf24; /* bg-yellow-500 */
  color: white;
  border: 1px solid transparent;
}
.btn-primary:hover {
  background-color: #f59e0b; /* hover:bg-yellow-600 */
}
.btn-primary:focus {
  --ring-shadow: 0 0 0 2px #fcd34d; /* focus:ring-yellow-400 */
}

.btn-secondary {
  background-color: white;
  color: #374151; /* text-gray-700 */
  border: 1px solid #d1d5db; /* border-gray-300 */
}
.btn-secondary:hover {
  background-color: #f9fafb; /* hover:bg-gray-100 */
}
.btn-secondary:focus {
  --ring-shadow: 0 0 0 2px #6366f1; /* focus:ring-indigo-500 */
}
</style>
